'use client';

import { Icon, Select, VisuallyHidden } from '@mage-ui/components';
import { cx } from '@mage-ui/styled-system/css';
import {
	comboboxSection,
	gridRow,
	icon,
	input,
	inputLabel,
	inputRoot,
	inputSection,
	selectButton,
	selectSection,
} from '@mage-ui/styled-system/recipes';
import { useContext } from 'react';
import { StaticTextContext } from '@/components/context/StaticTextProvider';
import { Instances } from '@/components/editora/Instances';
import { useQuery } from '@/services/client/use-query';
import { SelectBadge } from '../../controls/SelectBadge';
import { GridRow } from '../../layouts/grids/GridRow';
import { CourseSection } from '../../layouts/sections/CourseSection';
import { Loading } from '../../overlays/loading/Loading';
import { useCourseTypes } from './useCourseTypes';
import { useLanguage } from './useLanguage';
import { useLevel } from './useLevel';

export const CourseList = ({
	courseTypes,
	courses,
	languages,
	levels,
	formPage,
	currentLanguage,
}) => {
	const { st } = useContext(StaticTextContext);

	const { selectedCourseTypes, onCourseTypesChange } =
		useCourseTypes(courseTypes);
	const { selectedLanguage, onLanguageChange } = useLanguage(languages);
	const { selectedLevel } = useLevel(levels);

	const { data, isFetching } = useQuery({
		method: 'GET',
		url: '/api/courses',
		initialData: courses,
		language: currentLanguage,
		params: {
			language: selectedLanguage?.id || selectedLanguage?.slug || '',
			level: selectedLevel?.id || selectedLevel?.slug || '',
			course_types: selectedCourseTypes?.map((c) => c.slug),
		},
		options: {
			refetchOnWindowFocus: false,
		},
		fetchOptions: {
			tags: ['courses-list'],
		},
	});

	return (
		<CourseSection
			anchor={{ text: st('body.goto') }}
			filterTop={
				<Select
					label={st('register.select_language')}
					placeholder={selectedLanguage?.title ?? st('register.all_languages')}
					selectedOption={selectedLanguage}
					setSelectedOption={onLanguageChange}
					endSlot={
						<Icon name='arrow_drop_down' classNames={icon({ size: 's' })} />
					}
					classNames={{
						root: inputRoot({ direction: 'row' }),
						input: cx(
							input({ color: 'primary', size: 'xl', border: '100' }),
							selectButton(),
						),
						section: cx(inputSection(), comboboxSection(), selectSection()),
						label: inputLabel({
							color: 'primary',
							display: 'desktop',
							weight: 'bold',
						}),
					}}
					options={[
						{ id: '', value: st('register.all_languages') },
						...languages.map((l) => ({ id: l.slug, value: l.title })),
					]}
				/>
			} /*
      filterMid={
        <Select
          label={st('register.select_level')}
          placeholder={selectedLevel?.title ?? st('register.all_levels')}
          selectedOption={selectedLevel}
          setSelectedOption={onLevelChange}
          endSlot={
            <Icon name='arrow_drop_down' classNames={icon({ size: 's' })} />
          }
          classNames={{
            root: inputRoot({ direction: 'row' }),
            input: cx(
              input({ color: 'primary', size: 'xl', border: '100' }),
              selectButton(),
            ),
            section: cx(inputSection(), comboboxSection(), selectSection()),
            label: inputLabel({
              color: 'primary',
              display: 'desktop',
              weight: 'bold',
            }),
          }}
          options={[
            { id: '', value: st('register.all_levels') },
            ...levels.map((l) => ({ id: l.slug, value: l.title })),
          ]}
        />
      }*/
			filterBottom={
				<>
					<VisuallyHidden role='alert' aria-live='polite'>
						{st('register.select_course_type')}
					</VisuallyHidden>
					<SelectBadge
						data={{
							items: courseTypes.map((c) => ({
								label: c.title,
								value: c.title,
							})),
						}}
						setValue={onCourseTypesChange}
						texts={{
							placeholder: st('register.select_course_type'),
						}}
						selectedBadges={selectedCourseTypes?.map((c) => c.title)}
					/>
				</>
			}
		>
			{isFetching && (
				<Loading
					visible={isFetching}
					texts={{ loading: st('common.loading_information') }}
				/>
			)}
			{!isFetching && (
				<GridRow
					className={gridRow({
						gapM: 'gap200',
						gapL: 'gap300',
					})}
				>
					<Instances
						instances={data}
						formPage={formPage}
						language={currentLanguage}
					/>
				</GridRow>
			)}
		</CourseSection>
	);
};
